import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { IconTrash } from '@rossum/ui/icons/tabler';
import {
  Box,
  formControlClasses,
  outlinedInputClasses,
  Stack,
  SvgIcon,
} from '@rossum/ui/material';
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  GridEventListener,
} from '@rossum/ui/x-data-grid-pro';
import { useDataGridLocaleText } from '../../../../i18n/useDataGridLocaleText';
import { useFieldManagerDataContext } from '../../hooks/FieldManagerDataContext';
import { dataGridStyles } from '../field-detail/dataGridStyles';
import { filterPanelProps } from '../ui/dataGridStyles';
import { getColumns } from './columns';
import { colorWarning } from './dataGridStyles';
import { useMemoOverviewFields } from './hooks/useMemoOverviewFields';
import { useStoredFilterModel } from './hooks/useStoredFilterModel';
import { OverviewHeader } from './OverviewHeader';
import { DataForQueuesDialog, QueuesDialog } from './QueuesDialog';

export const Overview = () => {
  const { push } = useHistory();
  const { pathname } = useLocation();
  const intl = useIntl();
  const localeText = useDataGridLocaleText();
  const [storedFilterModel, setFilterModel] = useStoredFilterModel();

  const {
    flatSchemasWithQueues,
    schemas,
    queues,
    workspaces,
    isLoading: isLoadingFieldManagerData,
  } = useFieldManagerDataContext();

  const [rowForQueuesDialog, setRowForQueuesDialog] =
    useState<DataForQueuesDialog | null>(null);

  const columns: GridColDef[] = useMemo(
    () =>
      getColumns({
        setRowForQueuesDialog,
        intl,
      }),
    [intl]
  );

  const fields = useMemoOverviewFields(flatSchemasWithQueues);

  const handleRowClick: GridEventListener<'rowClick'> = params =>
    push(`${pathname}/detail/${params.row.fieldId}`);

  return (
    <PageLayout
      fullWidth
      renderHeader={params => (
        <OverviewHeader
          schemas={schemas}
          queues={queues}
          workspaces={workspaces}
          isLoadingFieldManagerData={isLoadingFieldManagerData}
          {...params}
        />
      )}
    >
      <Stack px={4} py={4}>
        <Box
          sx={{
            ...dataGridStyles,
            position: 'relative',
          }}
        >
          <DataGridPro
            disableRowSelectionExcludeModel
            onRowClick={handleRowClick}
            disableColumnSelector
            hideFooter
            autoHeight
            loading={isLoadingFieldManagerData}
            localeText={localeText}
            columns={columns}
            slots={{
              filterPanelDeleteIcon: () => (
                <SvgIcon fontSize="medium">
                  <IconTrash />
                </SvgIcon>
              ),
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'linear-progress',
              },
              baseSelectOption: {
                // @ts-expect-error sx is not in the type definition but it works,
                sx: {
                  // workaround for making first empty option having the same height as other options
                  '&:after': { content: "' '" },
                },
              },
              baseFormControl: {
                sx: {
                  // workaround for making singleSelect input filter full width
                  [`> * .${formControlClasses.root}`]: {
                    width: '100%',
                  },
                },
              },
              baseSelect: {
                native: false,
                // @ts-expect-error sx is not in the type definition but it works,
                sx: {
                  [`.${gridClasses.cell} & .${outlinedInputClasses.notchedOutline}`]:
                    {
                      border: 'unset !important',
                    },

                  [`& .${outlinedInputClasses.input}`]: {
                    pl: 2,
                  },
                },
              },
              filterPanel: filterPanelProps,
            }}
            rows={fields}
            disableColumnReorder
            disableRowSelectionOnClick
            initialState={{ filter: { filterModel: storedFilterModel } }}
            onFilterModelChange={setFilterModel}
            sx={{
              ...commonDataGridStyles,
              backgroundColor: 'transparent',
              [`& .${gridClasses.columnHeader}`]: {
                backgroundColor: 'transparent',
              },
              border: 'none',
              cursor: 'pointer',
              '.MuiDataGrid-cell:focus': {
                outline: 'none',
              },

              '.MuiDataGrid-columnHeader:focus': {
                outline: 'none',
              },
            }}
            getCellClassName={params =>
              params.value &&
              Array.isArray(params.value) &&
              params.value.length > 1
                ? colorWarning
                : ''
            }
          />
        </Box>
        {rowForQueuesDialog !== null && (
          <QueuesDialog
            rowForQueuesDialog={rowForQueuesDialog}
            setRowForQueuesDialog={setRowForQueuesDialog}
          />
        )}
      </Stack>
    </PageLayout>
  );
};
